import { GitHubRepository } from "@back4app2/sdk/lib/Back4app2";
import { ReactComponent as ErrorIcon } from "../../assets/images/error-icon.svg";
import { ReactComponent as GhostIcon } from "../../assets/images/ghost-icon.svg";
import { ReactComponent as GithubIcon } from "../../assets/images/github-logo.svg";
import { ReactComponent as B4AIconSVG } from "../../assets/images/b4a-sqaure-glow-logo.svg";

import { timeFromNow } from "../../utils/dates";
import MessageContainer from "./MessageContainer/MessageContainer";
import Skeleton from "../Skeleton";
import { DsButton } from '../ds';
import { ChevronDownIcon } from '../ds/icons';

interface IntegrateGithubProps {
  githubRepos: GitHubRepository[] | undefined,
  isImporting: Boolean,
  errorMsg: string | undefined,
  onImport: (repository: GitHubRepository) => void,
  /** The repository whose row is expanded, if any. */
  openRepositoryId?: string,
  /** What that row expands into — the branch and folder to build from. */
  openSlot?: React.ReactNode,
  filterText: string | undefined;
  isLoadingRepository: boolean
}

/**
 * The repository list as the user has narrowed it.
 *
 * Exported because the search box that supplies `filterText` lives one level
 * up now, in the wizard, and it reports how many results it found. Two copies
 * of this predicate would agree until the day one of them learned to match on
 * something else, and the count would then quietly disagree with the list
 * right below it.
 *
 * The user's text goes into a RegExp unescaped, which is how this has always
 * behaved: a stray `(` throws, so the call is guarded rather than the pattern
 * rewritten — changing the matching semantics is a different decision from
 * moving the function.
 */
export const filterRepositories = (
  repositories: GitHubRepository[],
  filterText: string | undefined
): GitHubRepository[] => {
  if (!filterText) {
    return repositories;
  }

  let regex: RegExp;

  try {
    regex = new RegExp(filterText, 'ig');
  } catch (e) {
    // Mid-typing `foo(` is not an error the user should see — it is a search
    // that has not finished being written. Match nothing until it parses.
    return [];
  }

  return repositories.filter((repository) => repository.fullName.match(regex));
};

const IntegrateGithub = ({ githubRepos, isImporting, errorMsg, onImport, filterText, isLoadingRepository, openRepositoryId, openSlot }: IntegrateGithubProps) => {

  if (isImporting) {
    return <>
      <div className="border-b border-b-regal-blue px-7 py-20">
        <div className="flex justify-center items-center overflow-visible h-6 mb-4 z-[-1]">
          <GithubIcon width="24px" height="24px" />
          <div className="flex space-x-2 ml-5">
            <span className="w-1 h-1 rounded bg-[#D9D9D9] animate-dot-flashing"></span>
            <span className="w-1 h-1 rounded bg-[#D9D9D9] animate-[dot-flashing_1s_infinite_0.5s_alternate] "></span>
            <span className="w-1 h-1 rounded bg-[#D9D9D9] animate-[dot-flashing_1s_infinite_1s_alternate] "></span>
          </div>
          <B4AIconSVG width="76px" height="76px" />
        </div>
        <div className="font-sora font-semibold text-lg leading-140 text-center">Loading repositories</div>
      </div>
    </>
  }

  if (isLoadingRepository) {
    return <>
      {Array(4).fill(0).map((_value, index) => <GithubRepositoryItem key={index} loading={isLoadingRepository} onImport={onImport} />)}
      <MessageContainer
        layout="row"
        msgTitle=""
        msgDesc="Not seeing a repository? Grant access on GitHub and it shows up here instantly."
        showButton={true}
        buttonText="Edit GitHub permissions"
        buttonAction="EDIT"
      />
    </>
  }

  if (errorMsg) {
    return <MessageContainer 
      msgTitle={errorMsg} 
      primaryIcon={<ErrorIcon />} 
      msgDesc={"Please try again."} 
      showButton={true}
      buttonText="Import GitHub Repo"
      buttonAction="IMPORT"
    />
  }

  if (!githubRepos || githubRepos.length === 0) {
    return <MessageContainer 
      msgTitle={"Nothing here, yet!"} 
      primaryIcon={<GhostIcon />}
      showButton={true}
      buttonText="Import GitHub Repo"
      buttonAction="IMPORT"
    />
  }

  const filteredRepositoryList = filterRepositories(githubRepos, filterText);

  // The search found nothing, but the account has repositories — so this is
  // not the "nothing here yet" state above, and rendering an empty list under
  // a box with text in it looks like a list that failed to load. The likeliest
  // reason a repository you can name is missing is that it belongs to an
  // organization this installation was never granted, which is why the way
  // out is the same button as everywhere else on this step.
  if (filterText && filteredRepositoryList.length === 0) {
    return <MessageContainer
      msgTitle={`No repository matches "${filterText}"`}
      primaryIcon={<GhostIcon />}
      msgDesc="If it belongs to an organization or to another account, grant access to it on GitHub and it shows up here instantly."
      showButton={true}
      buttonText="Edit GitHub permissions"
      buttonAction="EDIT"
    />
  }

  return (
    <>
      {filteredRepositoryList.map(repo => {
        return(
          <GithubRepositoryItem key={repo.id}
            loading={isLoadingRepository} 
            repositoryData={{...repo}}
            onImport={onImport} 
            isOpen={repo.id === openRepositoryId}
            // Under the row it belongs to, never floating over the list: the
            // question it asks is about THAT repository, and an overlay would
            // cover the neighbours you are still comparing it against.
            openSlot={repo.id === openRepositoryId ? openSlot : undefined}
          />
        )}
      )}
    </>
  )
}

export default IntegrateGithub

const GithubRepositoryItem = (props: {
  loading: boolean,
  repositoryData?: GitHubRepository,
  onImport: (repository: GitHubRepository) => void,
  isOpen?: boolean,
  openSlot?: React.ReactNode,
}) => {
  let { loading, repositoryData, onImport, isOpen, openSlot } = props;
  const optionsId = repositoryData
    ? `github-repository-${repositoryData.id}-options`
    : undefined;

  // Select is the only thing that selects. The row carries a repository name
  // that is itself a link to GitHub, so a row-wide click would sit on top of
  // it — and the two do opposite things: one leaves for another site, the
  // other commits you to a deploy. A row where the whole surface acts, except
  // for one word in the middle that acts differently, is a row people
  // mis-click exactly once and then stop trusting.
  return <div className={`border-b border-navy-700 ${isOpen ? 'bg-navy-800/60' : ''}`}>
  <div
    className="flex justify-between items-center gap-4 py-4 px-4 lg:px-5"
  >
    <div className="flex items-center min-w-0 flex-1">
      <GithubIcon width="18px" height="18px" className={`flex-none ${loading ? 'text-navy-400' : 'text-navy-200'}`} />
      <span className="min-w-0 ml-3">
        <span className="block text-ds-135 font-medium leading-[1.4] text-white truncate">
          {loading ? <Skeleton className="h-4 w-80" /> : (
            <a
              href={`https://www.github.com/${repositoryData?.fullName}`}
              target="_blank"
              rel="noopener noreferrer"
              className="b4-focus hover:text-action-soft"
              title={repositoryData?.fullName}
            >
              {/* The owner goes on a phone: truncated, every row read
                  "back4app-dev/…" and the part that tells them apart was cut. */}
              <span className="hidden sm:inline">{repositoryData?.fullName}</span>
              <span className="sm:hidden">{repositoryData?.fullName?.split('/').pop()}</span>
            </a>
          )}
        </span>
        {/* Branch and last push, in mono — the two facts that tell you whether
            this is the repository you meant. */}
        {!loading && (
          <span className="flex items-center gap-2 mt-1 font-courrier text-ds-115 leading-[1.4] text-navy-300">
            {repositoryData?.defaultBranch && <span className="min-w-[3ch] truncate">{repositoryData.defaultBranch}</span>}
            {repositoryData?.defaultBranch && repositoryData?.pushedAt && <span>·</span>}
            {/* "pushed" goes on a phone: with it the line did not fit, and the
                branch name was the part that got cut to one letter. */}
            {repositoryData?.pushedAt && <span className="whitespace-nowrap"><span className="hidden sm:inline">pushed </span>{timeFromNow(repositoryData.pushedAt)}</span>}
          </span>
        )}
      </span>
    </div>
    <div className="flex items-center justify-end flex-none">
      {loading
        ? <Skeleton className="h-4 w-20" />
        : (
          // Outline, not green. Green is "publish", and five green buttons in
          // a list stop reading as actions and start reading as texture.
          <DsButton
            size="md"
            variant={isOpen ? 'subtle' : 'outline'}
            ariaExpanded={isOpen}
            ariaControls={isOpen ? optionsId : undefined}
            onClick={() => { repositoryData && onImport(repositoryData) }}
          >
            Select
            <ChevronDownIcon
              size={13}
              className={`transition-transform duration-[120ms] ${isOpen ? 'rotate-180' : ''}`}
            />
          </DsButton>
        )}
    </div>
  </div>
  {openSlot && (
    <div id={optionsId}>
      {openSlot}
    </div>
  )}
  </div>
}
